import OrderRuleStatusLabel from "@/features/line-sheet-sets/order-rule-status-label";
import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import { useEffect, useMemo, useState } from "react";
import {
  AgencyLineSheetSetRow,
  BuyerLineSheetSetRow,
} from "@/features/order-sheet-sets/line-sheet-set-row.type";
import { Flex, Spinner, Text, Tooltip } from "@chakra-ui/react";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import { LightLineSheetSet } from "@/features/line-sheet-sets/line-sheet-set.types";
import { lineSheetSetMapper } from "@/features/invoices/utils/line-sheet-set-mapper";
import AppGridCell from "@/features/order-sheet-sets/app-grid-cell";
import usePersistedLineSheetSetListQuery from "@/features/line-sheet-sets/hooks/use-persisted-line-sheet-set-list-query";
import { highlightPartialText } from "@/utils/highlightSearch";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppLabel from "@/features/ui/app-label";
import CopyLineSheetSetModal from "@/features/line-sheet-sets/copy-line-sheet-set-modal";

interface LineSheetSetGridProps {
  isPendingLineSheetSetRefreshing?: boolean;
  resources?: LightLineSheetSet[];
  mutateResources: () => void;
}

export default function AgencyLineSheetSetGrid({
  isPendingLineSheetSetRefreshing,
  resources,
  mutateResources,
}: LineSheetSetGridProps) {
  const [rows, setRows] = useState<AgencyLineSheetSetRow[]>();
  const rowMapper = useMemo(() => {
    return lineSheetSetMapper.copy();
  }, []);

  useEffect(() => {
    setRows((prev) => {
      return rowMapper.toRows(resources, prev);
    });
  }, [resources, rowMapper]);

  const { navigateToLineSheetSetPage } = useRouterHelper();
  const { value: query } = usePersistedLineSheetSetListQuery();
  const { tTitle, tCurrencyString } = useI18nHelper();

  const lineSheetSetGrid = useLineSheetSetGrid({
    rows,
    mutateRows: mutateResources,
  });

  return (
    <>
      <AppGrid
        rows={rows}
        metas={[
          {
            isCheckable: false,
            rowType: "LineSheetSet",
            showCheckAll: false,
            gap: "4px",
            columns: [
              {
                ...lineSheetSetGrid.pairs.lineSheetSetType.column,
                width: "52px",
              },
              { ...lineSheetSetGrid.pairs.season.column, width: "52px" },
              {
                ...lineSheetSetGrid.pairs.brands.column,
                width: "104px",
              },
              {
                ...lineSheetSetGrid.pairs.issuedByBoutique.column,
                width: "144px",
              },
              {
                ...lineSheetSetGrid.pairs.title.column,
                width: "minmax(224px, 1fr)",
              },
              { ...lineSheetSetGrid.pairs.uploadedOn.column, width: "88px" },
              { ...lineSheetSetGrid.pairs.publishedOn.column, width: "88px" },
              { ...lineSheetSetGrid.pairs.validUntil.column, width: "152px" },
              { ...lineSheetSetGrid.pairs.status.column, width: "100px" },
              {
                name: tTitle("review_status"),
                width: "120px",
              },
            ],
            fields: [
              lineSheetSetGrid.pairs.lineSheetSetType.field,
              lineSheetSetGrid.pairs.season.field,
              {
                name: "Brand",
                value: (r) => {
                  const brands = (r as AgencyLineSheetSetRow).brands;
                  const brandList = brands
                    ?.map((brand) => brand.name)
                    .join(", ");
                  const minW =
                    brandList && brandList.length > 3000
                      ? 1600
                      : brandList && brandList.length > 1000
                      ? 800
                      : undefined;
                  return (
                    <Tooltip
                      label={brandList}
                      hasArrow={true}
                      placement={"top"}
                      openDelay={500}
                      minW={minW}
                    >
                      <Text noOfLines={2} whiteSpace="normal">
                        {highlightPartialText(brandList, [query.query])}
                      </Text>
                    </Tooltip>
                  );
                },
              },
              lineSheetSetGrid.pairs.issuedByBoutique.field,
              {
                ...lineSheetSetGrid.pairs.title.field,
                value: (_row, context) => {
                  const row = _row as AgencyLineSheetSetRow;
                  return (
                    <AppGridCell
                      value={[
                        lineSheetSetGrid.pairs.title.field.value(_row, context),
                        row.orderedAmount ? (
                          <Text fontSize={"11px"} color={"#8F8F8C"}>
                            {tCurrencyString(row.orderedAmount, {
                              type: "SYMBOL",
                              code: row.currency,
                            })}
                          </Text>
                        ) : (
                          ""
                        ),
                      ]}
                    />
                  );
                },
              },
              {
                ...lineSheetSetGrid.pairs.uploadedOn.field,
                chakraProps: { fontWeight: "700" },
              },
              {
                ...lineSheetSetGrid.pairs.publishedOn.field,
                chakraProps: { fontWeight: "700" },
              },
              {
                ...lineSheetSetGrid.pairs.validUntil.field,
                chakraProps: { fontWeight: "700" },
              },
              {
                name: "status",
                value: (_row, context) => {
                  const row = _row as AgencyLineSheetSetRow;

                  if (
                    isPendingLineSheetSetRefreshing &&
                    (row.status === "PARSING_PENDING" ||
                      row.compressionStatus === null ||
                      row.compressionStatus === "PENDING")
                  ) {
                    return <Spinner size={"sm"} />;
                  }

                  return (
                    <AppGridCell
                      value={[
                        lineSheetSetGrid.pairs.status.field.value(row, context),
                        lineSheetSetGrid.pairs.compressionStatus.field.value(
                          row,
                          context
                        ),
                      ]}
                    />
                  );
                },
              },
              {
                name: "reviewState",
                value: (_row) => {
                  const row = _row as AgencyLineSheetSetRow;
                  const isImageChecked = row.aiImageCompletedAt != null;
                  // 프리오더(SEASON)는 컨디션 적용 뱃지를 표시하지 않는다.
                  const isPreOrder = row.type === "SEASON";
                  return (
                    <Flex flexDirection={"column"} gap={"4px"}>
                      <AppLabel
                        nameKey={
                          isImageChecked
                            ? "common:image_check_completed"
                            : "common:image_check_required"
                        }
                        level={isImageChecked ? "SUCCESS" : "ERROR"}
                        isToUpper={false}
                      />
                      <OrderRuleStatusLabel
                        status={row.orderRuleStatus}
                        isHidden={isPreOrder}
                        fallbackAppliedAt={row.applyConditionCompletedAt}
                        fallbackHasOrderRule={
                          (row.orderRules?.some((r) => r.trim() !== "") ??
                            false) || (row.orderRuleFiles?.length ?? 0) > 0
                        }
                      />
                    </Flex>
                  );
                },
              },
            ],
            actions: [
              lineSheetSetGrid.actions.downloadCompressedImportedFile,
              lineSheetSetGrid.actions.downloadCompressedUnoFile,
              lineSheetSetGrid.actions.reportLineSheetSet,
              lineSheetSetGrid.actions.publishLineSheetSet,
              lineSheetSetGrid.actions.reparseLineSheetSet,
              lineSheetSetGrid.actions.pendLineSheetSetReviewStatus,
              lineSheetSetGrid.actions.approveLineSheetSetReviewStatus,
              lineSheetSetGrid.actions.rejectLineSheetSetReviewStatus,
              lineSheetSetGrid.actions.deleteLineSheetSet,
              lineSheetSetGrid.actions.copyLineSheetSet,
              lineSheetSetGrid.actions.restoreLineSheetSet,
            ],
            indicator: (row) => {
              return (
                <GridRowIndicator
                  type={"VIEW"}
                  isHovered={row.isHovered}
                  isCollapsed={row.isCollapsed}
                />
              );
            },
            onClick: (row) => {
              const lineSheetSetRow = row as AgencyLineSheetSetRow;
              localStorage.setItem("selectedLineSheetSetRow", row._rowId);
              navigateToLineSheetSetPage(
                lineSheetSetRow.id,
                query.query,
                query["madeByCompany.id__in"],
                query.season__in
              );
            },
          },
        ]}
      />
      {lineSheetSetGrid.disclosureCopyLineSheetSetModal.data && (
        <CopyLineSheetSetModal
          {...lineSheetSetGrid.disclosureCopyLineSheetSetModal.data}
          isOpen={lineSheetSetGrid.disclosureCopyLineSheetSetModal.isOpen}
          onClose={lineSheetSetGrid.disclosureCopyLineSheetSetModal.onClose}
        />
      )}
    </>
  );
}
